import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Progress } from '@backstage/core-components';
import { Campaign } from '@internal/backstage-plugin-bidone-backend';
import {
  useCampaignMembership,
  useFilter,
  useFilterPreview,
  useFreezeMembership,
} from '../../hooks/useCampaigns';

type SnapshotCurateDialogProps = {
  open: boolean;
  campaign: Campaign;
  onClose: () => void;
};

// Curate-then-freeze: the campaign's filter proposes candidates (all
// pre-checked, or the existing frozen set when re-curating); the user unchecks
// any out of scope; confirming persists the chosen set as frozen membership.
export const SnapshotCurateDialog = ({
  open,
  campaign,
  onClose,
}: SnapshotCurateDialogProps) => {
  const { data: filter } = useFilter(campaign.filterId ?? null);
  const previewInput = useMemo(
    () =>
      filter
        ? { combine: filter.combine, predicates: filter.predicates }
        : null,
    [filter],
  );
  const { data: preview, isFetching } = useFilterPreview(previewInput);
  const { data: membership } = useCampaignMembership(open ? campaign.id : null);
  const freeze = useFreezeMembership();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && preview) {
      if (membership && membership.length > 0) {
        setSelected(new Set(membership.map(m => m.componentRef)));
      } else {
        setSelected(new Set(preview.matches.map(m => m.ref)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preview, membership]);

  const toggle = (ref: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  const handleFreeze = async () => {
    await freeze.mutateAsync({
      campaignId: campaign.id,
      refs: Array.from(selected),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Curate snapshot membership</DialogTitle>
      <DialogContent>
        {!campaign.filterId && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Attach a targeting filter first so it can propose candidates.
          </Alert>
        )}
        {freeze.error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {(freeze.error as Error).message}
          </Alert>
        )}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          The filter proposes these components. Uncheck any that shouldn't be in
          scope, then freeze. {selected.size} selected.
        </Typography>
        {isFetching && !preview ? (
          <Progress />
        ) : (
          <List dense>
            {(preview?.matches ?? []).map(m => (
              <ListItem key={m.ref} disablePadding>
                <ListItemButton onClick={() => toggle(m.ref)} dense>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selected.has(m.ref)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={m.title}
                    secondary={m.owner ?? undefined}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={freeze.isPending || selected.size === 0}
          onClick={() => {
            handleFreeze().catch(() => undefined);
          }}
        >
          {freeze.isPending
            ? 'Freezing…'
            : `Freeze ${selected.size} component${selected.size === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
